<script lang="ts">
  import { onDestroy } from 'svelte'
  import { importDialog, closeImportDialog } from './stores'
  import DropZone from './DropZone.svelte'
  import { parseImport } from './parse'
  import { persistImport } from './persist'
  import { navigate } from '$lib/router'
  import { refreshCityList } from '../city-list'

  let text = $state('')
  let errorMessage = $state<string | null>(null)
  let busy = $state(false)
  let dialog: HTMLDialogElement | undefined = $state()

  const unsub = importDialog.subscribe(async (s) => {
    if (!dialog) return
    if (s.open && !dialog.open) dialog.showModal()
    if (!s.open && dialog.open) dialog.close()
    if (s.prefillFile) {
      // Read the prefill file and put its contents in the textarea.
      try {
        text = await s.prefillFile.text()
      } catch (err) {
        errorMessage = `Couldn't read that file: ${err instanceof Error ? err.message : err}`
      }
    } else {
      text = ''
      errorMessage = null
    }
  })

  onDestroy(() => unsub())

  async function onFile(file: File) {
    try {
      text = await file.text()
      errorMessage = null
    } catch (err) {
      errorMessage = `Couldn't read that file: ${err instanceof Error ? err.message : err}`
    }
  }

  async function submit() {
    if (busy) return
    errorMessage = null
    busy = true
    // Yield once so the spinner has a chance to render before parse blocks.
    await new Promise((r) => setTimeout(r, 0))
    try {
      const result = parseImport(text)
      if (!result.ok) {
        errorMessage = result.message
        return
      }
      const { planId } = await persistImport(result)
      await refreshCityList()
      closeImportDialog()
      navigate({ name: 'city', planId })
    } catch (err) {
      errorMessage = `Something went wrong saving the city: ${
        err instanceof Error ? err.message : err
      }`
    } finally {
      busy = false
    }
  }

  function onCancel(ev: Event) {
    ev.preventDefault()
    if (busy) return
    closeImportDialog()
  }
</script>

<dialog
  bind:this={dialog}
  oncancel={onCancel}
  class="w-[min(640px,90vw)] rounded-xl border border-border-rule bg-surface-card p-0 text-text-primary backdrop:bg-black/60"
>
  <form method="dialog" class="p-6" onsubmit={(e) => e.preventDefault()}>
    <header class="flex items-start justify-between">
      <div>
        <h2 class="text-xl font-semibold">Load city</h2>
        <p class="text-sm text-text-secondary">
          Drop a file from FoE Helper, or paste your city data directly.
        </p>
      </div>
      <button
        type="button"
        class="rounded p-1 text-text-secondary hover:bg-surface-card-recessed hover:text-text-primary"
        onclick={onCancel}
        aria-label="Close">✕</button
      >
    </header>

    <div class="mt-4 space-y-3">
      <DropZone {onFile} />

      <textarea
        class="h-40 w-full rounded-lg border border-input-border bg-input-bg p-3 font-mono text-xs text-input-text placeholder-input-placeholder focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent-bright"
        placeholder={'Or paste your city data here ({"CityMapData":...})'}
        bind:value={text}
        disabled={busy}
      ></textarea>

      {#if errorMessage}
        <p class="rounded-md border border-validation-error/50 bg-validation-error/15 p-3 text-sm text-validation-error">
          {errorMessage}
        </p>
      {/if}
    </div>

    <footer class="mt-6 flex items-center justify-end gap-3">
      <button
        type="button"
        class="rounded-md px-4 py-2 text-sm text-text-secondary hover:bg-surface-card-recessed"
        onclick={onCancel}
        disabled={busy}>Cancel</button
      >
      <button
        type="button"
        class="flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-accent-bright disabled:cursor-not-allowed disabled:bg-surface-2"
        onclick={submit}
        disabled={busy || text.trim().length === 0}
      >
        {#if busy}
          <span
            class="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
            aria-hidden="true"
          ></span>
          Loading…
        {:else}
          Load city
        {/if}
      </button>
    </footer>
  </form>
</dialog>

<style>
  dialog {
    margin: auto;
  }
</style>
